import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { getgeneralledger, listaccounts, memberlist } from "../../../../services/admin";
import { toast } from "react-toastify";
import moment from "moment";
import { printDataTable } from "../../../../utils/printTable";

export default function GeneralLedger() {
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState("");
    const [startDate, setStartDate] = useState(moment().startOf('month').format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [ledgerData, setLedgerData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [memberMap, setMemberMap] = useState({});

    useEffect(() => {
        fetchAccounts();
        fetchMembers();
    }, []);

    const fetchAccounts = () => {
        const branchId = localStorage.getItem("branch");
        listaccounts(branchId).then((res) => {
            if (res.status === true) {
                setAccounts(res.data.details || []);
            }
        });
    };

    const fetchMembers = () => {
        const temp = new FormData();
        temp.append("branch", localStorage.getItem("branch"));
        memberlist(temp).then((res) => {
            if (res.status === true) {
                // Create a map of member ID to member name
                const map = {};
                res.data.forEach(member => {
                    // Map multiple potential ID fields just in case
                    if (member.id) map[member.id] = member.member_name;
                    if (member.member_id) map[member.member_id] = member.member_name;
                    if (member.member_no) map[member.member_no] = member.member_name;
                });
                setMemberMap(map);
            }
        }).catch((error) => {
            console.error("Error fetching members:", error);
        });
    };

    const fetchGeneralLedger = () => {
        if (!selectedAccount) {
            toast.error("Please select an account");
            return;
        }

        setLoading(true);
        const data = {
            account_id: selectedAccount,
            start_date: startDate,
            end_date: endDate,
        };

        getgeneralledger(data).then((res) => {
            if (res.status === true) {
                console.log("General Ledger Response:", res.data);
                setLedgerData(res.data);
            } else {
                toast.error(res.message || "Failed to fetch ledger");
            }
            setLoading(false);
        });
    };

    const handleGenerate = () => {
        fetchGeneralLedger();
    };

    // window.print() printed the whole dashboard (sidebar, filters and all), so
    // the ledger is rebuilt as a clean sheet instead.
    const handlePrint = () => {
        if (!ledgerData) {
            return;
        }

        const drCr = (value) => (value >= 0 ? "Dr" : "Cr");

        printDataTable({
            title: "General Ledger",
            orientation: "landscape",
            meta: [
                getSelectedAccountName(),
                ledgerData.account ? `Type: ${ledgerData.account.account_type} | Group: ${ledgerData.account.account_group || "N/A"}` : "",
                ledgerData.period
                    ? `From ${moment(ledgerData.period.start_date).format("DD-MM-YYYY")} to ${moment(ledgerData.period.end_date).format("DD-MM-YYYY")}`
                    : "",
                `Opening Balance: ${formatCurrency(Math.abs(ledgerData.opening_balance))} ${drCr(ledgerData.opening_balance)}`,
                `Total Debit: ${formatCurrency(ledgerData.total_debit)} | Total Credit: ${formatCurrency(ledgerData.total_credit)}`,
                `Closing Balance: ${formatCurrency(Math.abs(ledgerData.closing_balance))} ${drCr(ledgerData.closing_balance)}`
            ],
            columns: [
                { header: "Date", value: (row) => (row.transaction_date ? moment(row.transaction_date).format("DD-MM-YYYY") : "") },
                { header: "Voucher No.", field: "voucher_number" },
                { header: "Narration", value: (row) => formatNarration(row.narration) },
                { header: "Debit (₹)", align: "right", value: (row) => (row.debit_amount > 0 ? formatCurrency(row.debit_amount) : "-") },
                { header: "Credit (₹)", align: "right", value: (row) => (row.credit_amount > 0 ? formatCurrency(row.credit_amount) : "-") },
                { header: "Balance (₹)", align: "right", value: (row) => formatCurrency(Math.abs(row.balance)) },
                { header: "Dr/Cr", align: "center", value: (row) => drCr(row.balance) }
            ],
            rows: ledgerData.entries || []
        });
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const getSelectedAccountName = () => {
        const account = accounts.find(a => a.id == selectedAccount);
        return account ? `${account.account_code} - ${account.account_name}` : '';
    };

    const formatNarration = (narration) => {
        if (!narration) return '';

        let formatted = narration;

        // More forgiving Pattern 1: "Cash paid for loan - 123", "Cash paid for loan-123", "Cash paid for loan 123", etc.
        formatted = formatted.replace(/(Cash\s*paid\s*for\s*loan)\s*-?\s*(\d+)/gi, (match, prefix, memberId) => {
            const memberName = memberMap[memberId];
            return memberName ? `${prefix} - ${memberName}` : match;
        });

        // Pattern 2: "Cash received from member 123" -> "Cash received from member John Doe"
        formatted = formatted.replace(/Cash received from member\s+(\d+)/gi, (match, memberId) => {
            const memberName = memberMap[memberId];
            return memberName ? `Cash received from member ${memberName}` : match;
        });

        // Pattern 3: "Loan disbursement - 123" -> "Loan disbursement - John Doe"
        formatted = formatted.replace(/Loan\s*disbursement\s*-?\s*(\d+)/gi, (match, memberId) => {
            const memberName = memberMap[memberId];
            return memberName ? `Loan disbursement - ${memberName}` : match;
        });

        // Pattern 4: "member 123" -> "member John Doe"
        formatted = formatted.replace(/member\s+(\d+)/gi, (match, memberId) => {
            const memberName = memberMap[memberId];
            return memberName ? `member ${memberName}` : match;
        });

        // Pattern 5: "- 123" at the end -> "- John Doe"
        formatted = formatted.replace(/\s-\s(\d+)$/g, (match, memberId) => {
            const memberName = memberMap[memberId];
            return memberName ? ` - ${memberName}` : match;
        });

        // Pattern 6: "loan - 123" -> "loan - John Doe"
        formatted = formatted.replace(/loan\s*-?\s*(\d+)/gi, (match, memberId) => {
            const memberName = memberMap[memberId];
            return memberName ? `loan - ${memberName}` : match;
        });

        return formatted;
    };

    const columnDefs = [
        {
            headerName: "Date",
            field: "transaction_date",
            width: 130,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => moment(params.value).format("DD-MM-YYYY")
        },
        {
            headerName: "Voucher No.",
            field: "voucher_number",
            width: 180,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" }
        },
        {
            headerName: "Narration",
            field: "narration",
            width: 350,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => formatNarration(params.value)
        },
        {
            headerName: "Debit (₹)",
            field: "debit_amount",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: (params) => ({
                textAlign: "right",
                color: params.value > 0 ? '#198754' : ''
            }),
            cellRenderer: (params) => {
                return params.value > 0 ? formatCurrency(params.value) : '-';
            }
        },
        {
            headerName: "Credit (₹)",
            field: "credit_amount",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: (params) => ({
                textAlign: "right",
                color: params.value > 0 ? '#dc3545' : ''
            }),
            cellRenderer: (params) => {
                return params.value > 0 ? formatCurrency(params.value) : '-';
            }
        },
        {
            headerName: "Balance (₹)",
            field: "balance",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: (params) => formatCurrency(Math.abs(params.value))
        },
        {
            headerName: "Dr/Cr",
            field: "balance",
            width: 120,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => {
                const isDebit = params.value >= 0;
                const badgeClass = isDebit ? 'bg-success' : 'bg-danger';
                const text = isDebit ? 'Dr' : 'Cr';
                return <span className={`badge ${badgeClass}`}>{text}</span>;
            }
        }
    ];

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">General Ledger</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Accounts</a>
                    </li>
                    <li className="breadcrumb-item">
                        <a>Reports</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        General Ledger
                    </li>
                </ol>
            </div>

            <div className="content-body">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <h4 className="card-title">General Ledger</h4>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            {ledgerData && (
                                                <button className="btn btn-success" onClick={handlePrint}>
                                                    <i className="fa fa-print"></i> Print
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                            <div className="row mb-4">
                                <div className="col-md-4">
                                    <label className="form-label">Account <span className="text-danger">*</span></label>
                                    <select
                                        className="form-control"
                                        value={selectedAccount}
                                        onChange={(e) => setSelectedAccount(e.target.value)}
                                    >
                                        <option value="">Select Account</option>
                                        {accounts.map((account) => (
                                            <option key={account.id} value={account.id}>
                                                {account.account_code} - {account.account_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2 d-flex align-items-end">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleGenerate}
                                        disabled={loading || !selectedAccount}
                                    >
                                        <i className="fa fa-search"></i> {loading ? 'Loading...' : 'Generate'}
                                    </button>
                                </div>
                            </div>

                            {ledgerData && (
                                <>
                                    <div className="text-center mb-4">
                                        <h5>General Ledger</h5>
                                        <p className="text-muted">
                                            Account: {getSelectedAccountName()}
                                        </p>
                                        {ledgerData.account && (
                                            <p className="text-muted">
                                                Type: <span className="badge bg-info">{ledgerData.account.account_type}</span>
                                                {' '}Group: <span className="badge bg-secondary">{ledgerData.account.account_group || 'N/A'}</span>
                                            </p>
                                        )}
                                        {ledgerData.period && (
                                            <p className="text-muted">
                                                From {moment(ledgerData.period.start_date).format('DD-MM-YYYY')}
                                                {' '}to {moment(ledgerData.period.end_date).format('DD-MM-YYYY')}
                                            </p>
                                        )}
                                    </div>

                                    {/* Opening Balance */}
                                    <div className="table-responsive mb-2">
                                        <table className="table table-bordered">
                                            <tbody>
                                                <tr className="table-info">
                                                    <td width="50%"><strong>Opening Balance</strong></td>
                                                    <td width="25%" className="text-end">
                                                        <strong>{formatCurrency(Math.abs(ledgerData.opening_balance))}</strong>
                                                    </td>
                                                    <td width="25%" className="text-center">
                                                        <span className={`badge ${
                                                            ledgerData.opening_balance >= 0 ? 'bg-success' : 'bg-danger'
                                                        }`}>
                                                            {ledgerData.opening_balance >= 0 ? 'Dr' : 'Cr'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Transactions Grid */}
                                    <div className="ag-theme-alpine ag-style">
                                        <AgGridReact
                                            columnDefs={columnDefs}
                                            rowData={ledgerData.entries || []}
                                            pagination={true}
                                            paginationPageSize={10}
                                            sortingOrder={["asc", "desc"]}
                                            suppressDragLeaveHidesColumns={true}
                                            domLayout="autoHeight"
                                        />
                                    </div>

                                    {/* Totals & Closing Balance */}
                                    {/* <div className="table-responsive mt-2">
                                        <table className="table table-bordered">
                                            <tbody>
                                                <tr className="table-secondary">
                                                    <td width="10%"><strong>Total</strong></td>
                                                    <td width="15%"></td>
                                                    <td width="35%"></td>
                                                    <td width="10%" className="text-end text-success">
                                                        <strong>{formatCurrency(ledgerData.total_debit)}</strong>
                                                    </td>
                                                    <td width="10%" className="text-end text-danger">
                                                        <strong>{formatCurrency(ledgerData.total_credit)}</strong>
                                                    </td>
                                                    <td width="10%"></td>
                                                    <td width="10%"></td>
                                                </tr>
                                                <tr className="table-primary">
                                                    <td colSpan="3"><strong>Closing Balance</strong></td>
                                                    <td></td>
                                                    <td></td>
                                                    <td className="text-end">
                                                        <strong>{formatCurrency(Math.abs(ledgerData.closing_balance))}</strong>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className={`badge ${
                                                            ledgerData.closing_balance >= 0 ? 'bg-success' : 'bg-danger'
                                                        }`}>
                                                            {ledgerData.closing_balance >= 0 ? 'Dr' : 'Cr'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div> */}

                                    {/* Summary Cards */}
                                    <div className="row mt-4">
                                        <div className="col-md-3">
                                            <div className="card bg-light">
                                                <div className="card-body text-center">
                                                    <h6 className="text-muted">Opening Balance</h6>
                                                    <h4>₹ {formatCurrency(Math.abs(ledgerData.opening_balance))}</h4>
                                                    <span className={`badge ${
                                                        ledgerData.opening_balance >= 0 ? 'bg-success' : 'bg-danger'
                                                    }`}>
                                                        {ledgerData.opening_balance >= 0 ? 'Debit' : 'Credit'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-success text-white">
                                                <div className="card-body text-center">
                                                    <h6>Total Debit</h6>
                                                    <h4>₹ {formatCurrency(ledgerData.total_debit)}</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-danger text-white">
                                                <div className="card-body text-center">
                                                    <h6>Total Credit</h6>
                                                    <h4>₹ {formatCurrency(ledgerData.total_credit)}</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-primary text-white">
                                                <div className="card-body text-center">
                                                    <h6>Closing Balance</h6>
                                                    <h4>₹ {formatCurrency(Math.abs(ledgerData.closing_balance))}</h4>
                                                    <span className="badge bg-light text-dark">
                                                        {ledgerData.closing_balance >= 0 ? 'Debit' : 'Credit'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {!ledgerData && !loading && (
                                <div className="text-center py-5">
                                    <i className="fa fa-book fa-3x text-muted mb-3"></i>
                                    <p className="text-muted">
                                        Select an account and click "Generate" to view the general ledger
                                    </p>
                                </div>
                            )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
